import { useCallback, useEffect, useRef, useState } from "react";
import { Lock } from "lucide-react";
import { api, apiError, setSessionLocked } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const IDLE_MS = 10 * 60 * 1000;
const EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

/** After 10 minutes of inactivity the current page is locked in place behind a blur and a
 *  password prompt — the user stays where they were and unlocks to continue (POST /auth/unlock,
 *  which re-checks the live server session). The Back button can never reveal the page underneath:
 *  while locked, any Back navigation sends the user to the Login screen and drops the session.
 *  The 10-minute idle and 12-hour absolute expiry remain enforced server-side (core._load_user). */
export function SessionLock({ children }) {
  const { user, setUser } = useAuth();
  const [locked, setLocked] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const lastPingAt = useRef(0);

  const lock = useCallback(() => {
    setLocked(true);
    setSessionLocked(true);
    // Trap the Back button: give it an entry to pop so popstate fires and we can send the user to login.
    try { window.history.pushState({ sessionLocked: true }, ""); } catch (e) { /* ignore */ }
  }, []);

  const toLogin = useCallback(async () => {
    setSessionLocked(false);
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    try { window.sessionStorage.clear(); } catch (e) { /* ignore */ }
    setUser(false);
    window.location.replace("/login?locked=1");
  }, [setUser]);

  const arm = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(lock, IDLE_MS);
  }, [lock]);

  useEffect(() => {
    if (!user || locked) { if (timer.current) clearTimeout(timer.current); return; }
    arm();
    const onActivity = () => {
      // Only genuine user interaction (mousemove/keydown/touch/scroll) reaches here, so the
      // idle timer — and the server-side idle window — are reset by real activity only. Background
      // polling, API responses and React renders never dispatch these events, so they cannot keep
      // an idle session alive or reset the lock. The server heartbeat is throttled (~55s) to avoid
      // a request per mouse move; it refreshes last_activity without extending absolute expiry.
      arm();
      if (Date.now() - lastPingAt.current < 55000) return;
      lastPingAt.current = Date.now();
      api.post("/auth/activity").catch(() => {});
    };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      if (timer.current) clearTimeout(timer.current);
    };
  }, [user, locked, arm]);

  // While locked, the Back button must land on Login, never restore the protected page.
  useEffect(() => {
    if (!locked) return;
    const onPop = () => { toLogin(); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [locked, toLogin]);

  const unlock = async (e) => {
    e.preventDefault();
    if (busy || !password) return;
    setBusy(true); setError("");
    try {
      await api.post("/auth/unlock", { password });
    } catch (err) {
      const msg = apiError(err);
      if (/incorrect password/i.test(msg)) { setError(msg); setBusy(false); return; }
      // Session genuinely gone (idle/absolute expiry) → force a full login.
      await toLogin();
      return;
    }
    setSessionLocked(false);
    setPassword("");
    setLocked(false); // stay on the same page and resume
    setBusy(false);
    arm();
  };

  return (
    <>
      <div className={locked ? "pointer-events-none select-none blur-lg" : ""} aria-hidden={locked} data-testid="session-lock-content">
        {children}
      </div>
      {locked && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-6 backdrop-blur-sm" data-testid="session-lock-overlay">
          <form onSubmit={unlock} className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-2xl text-center">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black text-[#EA580C]"><Lock className="h-5 w-5" /></span>
            <p className="overline mt-6 text-[#EA580C]">Session locked</p>
            <h2 className="font-ui mt-2 text-xl font-bold tracking-tight">Enter your password to continue</h2>
            <p className="mt-3 text-sm text-slate-600">You were inactive for 10 minutes. Your work is still here — unlock to resume.</p>
            <input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" data-testid="session-unlock-password"
              className="mt-5 w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-[#EA580C]" />
            {error && <p className="mt-2 text-sm text-red-600" data-testid="session-unlock-error">{error}</p>}
            <button type="submit" disabled={busy || !password} data-testid="session-unlock-submit"
              className="mt-4 w-full rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50 hover:bg-slate-800 transition-colors">
              {busy ? "Unlocking…" : "Unlock"}
            </button>
            <button type="button" onClick={toLogin} data-testid="session-unlock-logout"
              className="mt-3 text-xs text-slate-500 hover:text-slate-800">Sign in as a different user</button>
          </form>
        </div>
      )}
    </>
  );
}
